import { create } from 'zustand';
import { errorMessage, fetchLineInfo, fetchMachineDetail, fetchUtMatrixList } from '../api';
import { MAX_COMPARE } from '../constants';
import {
  AnalysisRow,
  ChatMessage,
  ColumnFilters,
  FilterValue,
  SortConfig,
  UtKey,
  UtMatrixOption,
} from '../types';
import { analyze } from '../utils/analyze';

interface UtComparisonState {
  // ── ① 비교 대상 UT Matrix 선택 ──
  selectedMatrices: UtMatrixOption[]; // 최대 MAX_COMPARE개, 순서 = 비교 순서(V1..V3)
  availableMatrices: UtMatrixOption[];
  loadingList: boolean;

  // ── 설정/필터 상태 ──
  currentUT: UtKey | 'ALL';
  diffOnly: boolean;
  perLineChecked: boolean;

  // ── 분석 결과 ──
  analysisResult: AnalysisRow[] | null;
  /** 분석 실행 시점에 확정된 버전 라벨 (UTM_NM 기준) */
  versionLabels: string[];
  /** 분석 실행 시점에 확정된 버전별 UT Matrix 버전 번호 (VER_NO) */
  versionVerNos: string[];
  /** 분석 실행 시점에 확정된 모드 (체크박스와 별개) */
  isPerLineMode: boolean;
  hasAnalyzed: boolean;
  analyzing: boolean;

  // ── ④ 분석표 정렬/필터 ──
  sortConfig: SortConfig;
  columnFilters: ColumnFilters;

  /** 저장된 리포트를 조회 중이면 해당 RPT_ID — 이 경우 대상 편집/재분석을 막는다 */
  historyRptId: string | null;
  /**
   * 이력 복원 시점의 원본 스냅샷 — "1라인별 사용량 비교"를 껐을 때 되돌릴 값.
   * 재분석 결과로 덮어쓰지 않는다.
   */
  historySnapshot: {
    rows: AnalysisRow[];
    versionLabels: string[];
    versionVerNos: string[];
  } | null;

  // ── 챗봇 ──
  chatHistory: ChatMessage[];

  // ── actions ──
  fetchAvailableMatrices: () => Promise<void>;
  addMatrix: (m: UtMatrixOption) => void;
  /** 외부 화면(UT Matrix Summary 등)에서 비교 대상을 통째로 주입 — 최대 MAX_COMPARE개 */
  setSelectedMatrices: (list: UtMatrixOption[]) => void;
  removeMatrix: (utmId: string) => void;
  /** 저장된 리포트(RPT_D)를 그대로 복원 — 재분석하지 않는다 */
  loadHistorySnapshot: (params: {
    rows: AnalysisRow[];
    targets: UtMatrixOption[];
    historyRptId: string;
  }) => void;
  setCurrentUT: (ut: UtKey | 'ALL') => void;
  setDiffOnly: (v: boolean) => void;
  setPerLineChecked: (v: boolean) => void;
  runAnalyze: () => Promise<void>;
  toggleSort: (colId: string) => void;
  setColumnFilter: (colId: string, values: Set<FilterValue> | null) => void;
  addChatMessage: (msg: ChatMessage) => void;
  reset: () => void;
}

const INITIAL_CHAT: ChatMessage[] = [
  {
    role: 'bot',
    text: '안녕하세요! 비교할 UT Matrix를 선택하고 "분석 시작"을 눌러주세요.\n분석 후에는 "CDA 합계 알려줘", "증가가 가장 큰 항목은?" 등을 물어보실 수 있습니다.',
  },
];

export const useUtComparisonStore = create<UtComparisonState>((set, get) => ({
  selectedMatrices: [],
  availableMatrices: [],
  loadingList: false,

  currentUT: 'ALL',
  diffOnly: false,
  perLineChecked: false,

  analysisResult: null,
  versionLabels: [],
  versionVerNos: [],
  isPerLineMode: false,
  hasAnalyzed: false,
  analyzing: false,

  sortConfig: { col: null, dir: 1 },
  columnFilters: {},

  historyRptId: null,
  historySnapshot: null,

  chatHistory: INITIAL_CHAT,

  fetchAvailableMatrices: async () => {
    set({ loadingList: true });
    try {
      const list = await fetchUtMatrixList();
      set({ availableMatrices: list });
    } catch (e) {
      window.alert('UT Matrix 목록 조회 오류: ' + errorMessage(e));
    } finally {
      set({ loadingList: false });
    }
  },

  addMatrix: (m) =>
    set((s) => {
      if (s.selectedMatrices.length >= MAX_COMPARE) {
        window.alert(`비교 대상 UT Matrix는 최대 ${MAX_COMPARE}개까지 선택할 수 있습니다.`);
        return s;
      }
      if (s.selectedMatrices.some((x) => x.utmId === m.utmId)) return s;
      return { selectedMatrices: [...s.selectedMatrices, m] };
    }),

  /** 수정됨 **/
  setSelectedMatrices: (list) => {
    const seen = new Set<string>();
    const next: UtMatrixOption[] = [];
    for (const m of list) {
      if (!m.utmId || seen.has(m.utmId)) continue;
      seen.add(m.utmId);
      next.push(m);
      if (next.length >= MAX_COMPARE) break;
    }
    set({ selectedMatrices: next });
  },
  /** 수정됨 **/

  removeMatrix: (utmId) =>
    set((s) => ({ selectedMatrices: s.selectedMatrices.filter((m) => m.utmId !== utmId) })),

  /** 저장된 리포트(RPT_D)를 그대로 복원 — 재분석하지 않으므로 원본 UT Matrix가 바뀌어도 결과가 고정된다 */
  loadHistorySnapshot: ({ rows, targets, historyRptId }) => {
    const diffCnt = rows.filter((r) => r.machineHasDiff).length;
    // ④ 비교 분석표는 기준(1번)/비교대상(2번) 2개 컬럼만 그린다 — 참조(3번)는 비교에
    // 참여하지 않고 ① 대상 목록에만 남는다(저장된 RPT_D도 2칸만 복원한다).
    const compareTargets = targets.slice(0, 2);
    const versionLabels = compareTargets.map((m) => m.utmNm || m.utmId);
    const versionVerNos = compareTargets.map((m) => m.verNo);
    set({
      historyRptId,
      // "1라인별" 토글을 껐을 때 되돌릴 원본 — 재분석 결과로 덮이지 않도록 따로 보관한다
      historySnapshot: { rows, versionLabels, versionVerNos },
      selectedMatrices: targets,
      analysisResult: rows,
      versionLabels,
      versionVerNos,
      perLineChecked: false,
      isPerLineMode: false,
      hasAnalyzed: true,
      analyzing: false,
      sortConfig: { col: null, dir: 1 },
      columnFilters: {},
      chatHistory: [
        {
          role: 'bot',
          text: `저장된 리포트를 불러왔습니다. 총 ${rows.length}개 항목 중 변동 ${diffCnt}개입니다.`,
        },
      ],
    });
  },

  setCurrentUT: (currentUT) => set({ currentUT }),
  setDiffOnly: (diffOnly) => set({ diffOnly }),
  /**
   * "1라인별 사용량 비교" 토글.
   *
   * 저장된 리포트를 보는 중(historyRptId)에는 스냅샷만으로 라인당 사용량을 구할 수 없다 —
   * TB_EELMB_UTM_RPT_D에 라인수량이 없어 복원 시 lineQty가 0으로 비어 있기 때문이다.
   * 그래서 켤 때는 원본 UT Matrix를 다시 읽어 재계산하고, 끌 때는 재분석하지 않고
   * 보관해둔 스냅샷으로 되돌린다(원본이 그 사이 바뀌었어도 저장 당시 값으로 복귀).
   *
   * 어느 쪽도 DB에 쓰지 않는다 — 조회 전용이라 저장된 이력은 그대로다.
   * 신규 분석 흐름(historyRptId 없음)은 기존대로 체크만 하고 "분석시작"에서 반영된다.
   */
  setPerLineChecked: (perLineChecked) => {
    set({ perLineChecked });
    const { historyRptId, historySnapshot } = get();
    if (!historyRptId) return;

    if (perLineChecked) {
      void get().runAnalyze();
      return;
    }
    if (!historySnapshot) return;
    set({
      analysisResult: historySnapshot.rows,
      versionLabels: historySnapshot.versionLabels,
      versionVerNos: historySnapshot.versionVerNos,
      isPerLineMode: false,
      sortConfig: { col: null, dir: 1 },
      columnFilters: {},
    });
  },

  runAnalyze: async () => {
    const { selectedMatrices } = get();
    if (selectedMatrices.length < 2) {
      window.alert('❌ 비교할 UT Matrix를 2개 이상 선택해주세요.');
      return;
    }
    let { perLineChecked } = get();
    set({ analyzing: true });
    try {
      // 분석은 기준(1번) ↔ 비교대상(2번) 2건만 사용한다. 참조(3번)는 ① 대상 목록에만 남고
      // 비교에는 참여하지 않는다 — analyze()가 "마지막 − 첫번째"로 차이를 내므로 3건을 넘기면
      // 비교대상이 아니라 참조와의 차이가 계산된다.
      const compareTargets = selectedMatrices.slice(0, 2);
      const utmIds = compareTargets.map((m) => m.utmId);
      const [rows, lineInfo] = await Promise.all([
        fetchMachineDetail(utmIds),
        perLineChecked ? fetchLineInfo(utmIds) : Promise.resolve([]),
      ]);

      if (perLineChecked && lineInfo.length === 0) {
        window.alert('❌ "1라인별 사용량 비교"를 사용하려면 라인정보 조회 결과가 있어야 합니다.');
        perLineChecked = false;
        set({ perLineChecked: false });
      }

      const result = analyze({ utmIds, rows, lineInfo, perLine: perLineChecked });

      if (result.unmatchedEqGroups.length > 0) {
        console.warn(
          '라인정보에 매칭되지 않은 설비군 (라인수량=1로 처리됨):',
          result.unmatchedEqGroups.join(', ')
        );
      }

      const diffCnt = result.rows.filter((r) => r.machineHasDiff).length;
      set({
        analysisResult: result.rows,
        versionLabels: compareTargets.map((m) => m.utmNm || m.utmId),
        versionVerNos: compareTargets.map((m) => m.verNo),
        isPerLineMode: result.isPerLineMode,
        hasAnalyzed: true,
        sortConfig: { col: null, dir: 1 },
        columnFilters: {},
        chatHistory: [
          {
            role: 'bot',
            text: `분석이 완료되었습니다. 총 ${result.rows.length}개 항목 중 변동 ${diffCnt}개입니다.\n사용량에 대해 무엇이든 물어보세요. (예: "CDA 합계 알려줘")`,
          },
        ],
      });
    } catch (e) {
      window.alert('분석 오류: ' + errorMessage(e));
    } finally {
      set({ analyzing: false });
    }
  },

  toggleSort: (colId) =>
    set((s) => {
      if (s.sortConfig.col === colId) {
        return { sortConfig: { col: colId, dir: (s.sortConfig.dir * -1) as 1 | -1 } };
      }
      return { sortConfig: { col: colId, dir: 1 } };
    }),

  setColumnFilter: (colId, values) =>
    set((s) => {
      const next = { ...s.columnFilters };
      if (values === null) delete next[colId];
      else next[colId] = values;
      return { columnFilters: next };
    }),

  addChatMessage: (msg) => set((s) => ({ chatHistory: [...s.chatHistory, msg] })),

  reset: () =>
    set({
      selectedMatrices: [],
      currentUT: 'ALL',
      diffOnly: false,
      perLineChecked: false,
      analysisResult: null,
      versionLabels: [],
      versionVerNos: [],
      isPerLineMode: false,
      hasAnalyzed: false,
      historyRptId: null,
      historySnapshot: null,
      sortConfig: { col: null, dir: 1 },
      columnFilters: {},
      chatHistory: [
        { role: 'bot', text: '초기화되었습니다. 비교할 UT Matrix를 다시 선택해주세요.' },
      ],
    }),
}));
